import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { useReadonlyField } from 'components/common-form/contexts/readonly/FieldMetadataContext';
import { SummaryFieldRow } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/SummaryFieldRow';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { CurrencySymbol } from 'components/shared/NewAmountInput/CurrencySymbol';
import { UnderlyingTag } from 'components/underlying-tag';
import { useAppSelector } from 'hooks/useAppSelector';
import type { IntlKey } from 'locales';
import { UnderlyingValueDefinition } from 'models/metadata/readonlyValue.definition';
import type { UnderlyingCodeType } from 'models/underlying/base';
import type { CommodityUnderlying } from 'models/underlying/commodity';
import type { CreditUnderlying } from 'models/underlying/credit';
import type { ForexUnderlying } from 'models/underlying/forex';
import { RateUnderlying } from 'models/underlying/rate';
import { ComponentProps, FC, useMemo } from 'react';
import { getUserPreferencesUnderlyingCodeType } from 'state/selectors';
import { getUnderlyingCode } from 'utils/underlying/underlying-code';

export interface ProductSummaryUnderlyingProps {
  node: UnderlyingValueDefinition;
}

const Table = ({ className, ...props }: ComponentProps<'table'>) => (
  <table {...props} className={classNames('table table-borderless text-center mb-1', className)} />
);

const TableContainer = ({ className, ...props }: ComponentProps<'div'>) => (
  <div {...props} className={classNames('border border-primary px-0', className)} />
);

export const ProductSummaryUnderlyingField: FC<ProductSummaryUnderlyingProps> = ({ node }) => {
  const defaultUnderlyingCodeType = useAppSelector(state => getUserPreferencesUnderlyingCodeType(state));
  const data = useReadonlyField().getMetadataWithData(node);

  const translationKey = node.translationKey ?? node.id;
  const showWeight = isDefined(data?.weight);

  const isEquity = useMemo<boolean>(() => data?.asset.localeCompare('Equity') === 0, [data]);
  const isForex = useMemo<boolean>(() => data?.asset.localeCompare('Forex') === 0, [data]);
  const isRate = useMemo<boolean>(() => data?.asset.localeCompare('Rate') === 0, [data]);

  const isCreditRateRefEntity = useMemo<boolean>(
    () => data?.asset.localeCompare('CreditRate') === 0 && data?.type.localeCompare('RefEntity') === 0,
    [data],
  );

  const isCreditRateIndexCredit = useMemo<boolean>(
    () => data?.asset.localeCompare('CreditRate') === 0 && data?.type.localeCompare('IndexCredit') === 0,
    [data],
  );

  function getUnderlyingLabelTranslationKey(defaultUnderlyingCodeType: UnderlyingCodeType): IntlKey {
    return defaultUnderlyingCodeType === 'Reuters' ? 'reuters' : 'bloomberg';
  }

  const commodityData = data as CommodityUnderlying | undefined;
  const forexData = data as ForexUnderlying | undefined;
  const rateData = data as RateUnderlying | undefined;
  const creditData = data as CreditUnderlying | undefined;

  return (
    <SummaryFieldRow labelKey={translationKey} id={node.id}>
      {isEquity && (
        <TableContainer className="w-75">
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-code">
                  <FormattedMessage id={getUnderlyingLabelTranslationKey(defaultUnderlyingCodeType)} />
                </th>
                <th data-e2e="underlying-header-strike">
                  <FormattedMessage id="productSummary.underlyingsGrid.strikeMode" />
                </th>
                {showWeight && (
                  <th data-e2e="underlying-header-weight">
                    <FormattedMessage id="productSummary.underlyingsGrid.weight" />
                  </th>
                )}
                <th data-e2e="underlying-header-spot">
                  <FormattedMessage id="productSummary.underlyingsGrid.strikeValue" />
                </th>
                <th data-e2e="underlying-header-valuation">
                  <FormattedMessage id="productSummary.underlyingsGrid.valuation" />
                </th>
              </tr>
            </thead>
            <tbody>
              {commodityData && (
                <tr key={commodityData.id} className="m-0 fw-light" data-e2e="underlying-row">
                  <td data-e2e="code">
                    <UnderlyingTag
                      value={commodityData}
                      label={getUnderlyingCode(commodityData, defaultUnderlyingCodeType)}
                      isReadOnly
                    />
                  </td>
                  <td data-e2e="strike">
                    {commodityData.strike !== undefined && (
                      <FormattedMessage
                        id={`form.StrikeMode.${commodityData.strike.strikeMode}` as any}
                        defaultMessage={commodityData.strike.strikeMode}
                      />
                    )}
                  </td>
                  {showWeight && (
                    <td data-e2e="weight">
                      <FormattedNumber value={commodityData.weight} maximumFractionDigits={3} />
                      {commodityData.isWeightInPercent && '%'}
                    </td>
                  )}
                  <td data-e2e="spot" className="text-center">
                    <CurrencySymbol code={commodityData.currency} />
                    {commodityData.strike?.strikeValue === undefined || commodityData.strike?.strikeValue === 0
                      ? ''
                      : ' ' + commodityData.strike?.strikeValue}
                  </td>
                  <td data-e2e="valuation">
                    {commodityData.strike?.strikeDate && (
                      <FormattedDate
                        value={commodityData.strike?.strikeDate as string}
                        year="numeric"
                        month="short"
                        day="2-digit"
                      />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {isCreditRateRefEntity && creditData && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-name">Name</th>
                <th data-e2e="underlying-header-seniority">Seniority</th>
                <th data-e2e="underlying-header-curve-name">Curve name</th>
              </tr>
            </thead>
            <tbody>
              <tr key={creditData.id} className="m-0 fw-light" data-e2e="underlying-row">
                <td data-e2e="name">{creditData.name}</td>
                <td data-e2e="seniority">
                  <FormattedMessage
                    id={`quote.underlyingsGrid.seniorityTypes.${creditData.seniorityType}` as any}
                    defaultMessage={creditData.seniorityType}
                  />
                </td>
                <td data-e2e="curve-name">{creditData.id}</td>
              </tr>
            </tbody>
          </Table>
        </TableContainer>
      )}

      {isCreditRateIndexCredit && creditData && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-name">Name</th>
                <th data-e2e="underlying-header-curve-name">Curve name</th>
              </tr>
            </thead>
            <tbody>
              <tr key={creditData.id} className="m-0 fw-light" data-e2e="underlying-row">
                <td data-e2e="name">{creditData.name}</td>
                <td data-e2e="curve-name">{creditData.id}</td>
              </tr>
            </tbody>
          </Table>
        </TableContainer>
      )}

      {isForex && forexData && (
        <div key={forexData.id} className="badge badge-outline-secondary badge-lg rounded-pill" data-e2e="name">
          <span className="text-primary">{forexData.name}</span>
        </div>
      )}
      {isRate && rateData && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-name">Name</th>
              </tr>
            </thead>
            <tbody>
              <tr key={rateData.id} className="m-0 fw-light" data-e2e="underlying-row">
                <td data-e2e="name">{rateData.name}</td>
              </tr>
            </tbody>
          </Table>
        </TableContainer>
      )}
    </SummaryFieldRow>
  );
};
